import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import EngagementCarouselSection from './EngagementCarouselSection';
import EngagementCarouselSkeleton from './EngagementCarouselSkeleton';
import EngagementCarouselError from './EngagementCarouselError';
import EngagementVideoCard from './EngagementVideoCard';

const EngagementCarousel = ({
  items,
  loading,
  refreshing,
  error,
  onRetry,
  className = '',
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 4);
    setCanScrollRight(left < max - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, items?.length]);

  const scrollByDirection = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.85, 240);
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  if (loading) {
    return <EngagementCarouselSkeleton />;
  }

  if (error) {
    return <EngagementCarouselError onRetry={onRetry} />;
  }

  if (!Array.isArray(items) || items.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[EngagementCarousel] No items returned. Hiding section.');
    }
    return null;
  }

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <EngagementCarouselSection className={className}>
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm sm:text-base md:text-lg font-bold text-foreground leading-tight truncate">
            {t('engagement_carousel.section_title', 'Featured Videos')}
          </h2>
          {refreshing ? (
            <span
              className="hidden sm:inline-block w-2 h-2 rounded-full bg-primary/50 animate-pulse"
              aria-hidden="true"
            />
          ) : null}
        </div>

        {hasOverflow && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => scrollByDirection(-1)}
              disabled={!canScrollLeft}
              aria-label={t(
                'engagement_carousel.scroll_prev',
                'Scroll videos left'
              )}
              className="
                w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-700
                flex items-center justify-center
                hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                transition-opacity
              "
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollByDirection(1)}
              disabled={!canScrollRight}
              aria-label={t(
                'engagement_carousel.scroll_next',
                'Scroll videos right'
              )}
              className="
                w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-700
                flex items-center justify-center
                hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                transition-opacity
              "
            >
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <div className="engagement-carousel relative">
        <div
          ref={scrollRef}
          className="
            flex gap-3 sm:gap-4
            overflow-x-auto pb-1
            snap-x snap-mandatory scroll-smooth
            scrollbar-hide
          "
        >
          {items.map((item, index) => (
            <div key={item.id || index} className="snap-start flex-shrink-0">
              <EngagementVideoCard item={item} position={index} />
            </div>
          ))}
        </div>

        {canScrollRight && (
          <div
            className="engagement-carousel-fade-right absolute right-0 top-0 bottom-1 w-6 sm:w-8 pointer-events-none"
            aria-hidden="true"
          />
        )}
        {canScrollLeft && (
          <div
            className="engagement-carousel-fade-left absolute left-0 top-0 bottom-1 w-6 sm:w-8 pointer-events-none"
            aria-hidden="true"
          />
        )}
      </div>

      <style>{`
        .engagement-carousel-fade-right {
          background: linear-gradient(to left,
            rgba(255, 255, 255, 0.7) 0%,
            rgba(255, 255, 255, 0) 100%
          );
        }
        .engagement-carousel-fade-left {
          background: linear-gradient(to right,
            rgba(255, 255, 255, 0.7) 0%,
            rgba(255, 255, 255, 0) 100%
          );
        }
      `}</style>
    </EngagementCarouselSection>
  );
};

export default EngagementCarousel;
